/** @jsx jsx */
import {
  Modal,
  ModalActionButton,
  ModalContainer,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalHeading,
  ModalProps,
  Paragraph,
} from "@eworkit/iris";
import { FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";

import { removeTimeReportProject, TimeReport } from "@/data/time-reports";
import { useToasts } from "@/views/components/Toast/useToasts";

import { useTimeReportEdit } from "../edit/TimeReportEditContext";

interface DeleteTimeReportProjectModalProps extends ModalProps {
  timeReport: TimeReport;
  projectGuid: string;
}

export const DeleteTimeReportProjectModal: FC<DeleteTimeReportProjectModalProps> = ({
  timeReport,
  projectGuid,
  ...modalProps
}) => {
  const { t } = useTranslation("session");
  const { addToast } = useToasts();

  const { updateTimeReport: update } = useTimeReportEdit();

  const [loading, setLoading] = useState(false);

  const handleDeleteClick = useCallback(async () => {
    try {
      setLoading(true);

      const newTimeReport = removeTimeReportProject(timeReport, projectGuid);
      await update(newTimeReport);

      modalProps.dialogState.hide?.();
    } catch (error) {
      addToast({
        text: t("common:ERROR_MESSAGES.GENERAL"),
        type: "danger",
      });
    } finally {
      setLoading(false);
    }
  }, [timeReport, update, addToast, modalProps.dialogState, projectGuid, t]);

  return (
    <Modal {...modalProps} aria-label={t("TIME_REPORTS.PROJECTS.DELETE_MODAL_HEADER")}>
      <ModalContainer>
        <ModalHeader
          headerPlaceholder={<ModalHeading>{t("TIME_REPORTS.PROJECTS.DELETE_MODAL_HEADER")}</ModalHeading>}
          onCloseClick={modalProps.dialogState.hide}
          closeButtonAriaLabel={t("COMPONENTS.MODAL.CLOSE")}
        />

        <ModalContent>
          <Paragraph>{t("TIME_REPORTS.PROJECTS.ARE_YOU_SURE_YOU_WANT_TO_DELETE")}</Paragraph>
        </ModalContent>

        <ModalFooter>
          <ModalActionButton type="button" variant="ghost" onClick={modalProps.dialogState.hide}>
            {t("TIME_REPORTS.PROJECTS.CANCEL")}
          </ModalActionButton>
          <ModalActionButton
            type="submit"
            variant="primary-danger"
            onClick={handleDeleteClick}
            loading={loading}
            disabled={loading}
            focusable={!loading}>
            {t("TIME_REPORTS.PROJECTS.DELETE")}
          </ModalActionButton>
        </ModalFooter>
      </ModalContainer>
    </Modal>
  );
};
